/* eslint-disable @typescript-eslint/no-floating-promises */

// TODO: Fix ESLint errors and improve type safety

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useFilterStore } from '../store/filter-store';
import { FilterSection } from './FilterSection';
import { AvailabilityFilter } from './AvailabilityFilter';
import { ChargeSpeedFilter } from './ChargeSpeedFilter';
import { PriceFilter } from './PriceFilter';
import { OperatorFilter } from './OperatorFilter';
import { LocationBlock } from './LocationBlock';
import { Switch } from '@/components/ui/Switch';
import { useLocationInformation } from '@/features/location/hooks/useLocationInformation';
import { buildOperatorList } from '../utils/monta-operator';
import * as Accordion from '@radix-ui/react-accordion';

interface FilterPanelProps {
  /**
   * When rendered inside the mobile BottomSheet, the sheet owns the scroll,
   * title, and close button — so the panel drops its card chrome, header, and
   * internal max-height/overflow to avoid a nested double-scroll.
   */
  inBottomSheet?: boolean;
}

/**
 * Main filter panel with all filter sections
 */
export function FilterPanel({ inBottomSheet = false }: FilterPanelProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const showAutoCharge = useFilterStore((s) => s.showAutoCharge);
  const setShowAutoCharge = useFilterStore((s) => s.setShowAutoCharge);
  const showAutoChargeFilter = useFilterStore((s) => s.showAutoChargeFilter);
  const showOperatorFilter = useFilterStore((s) => s.showOperatorFilter);
  const showLocationDetails = useFilterStore((s) => s.showLocationDetails);
  const showPriceFilter = useFilterStore((s) => s.showPriceFilter);
  const hasActiveFilters = useFilterStore((s) => s.hasActiveFilters());
  const resetFilters = useFilterStore((s) => s.resetFilters);

  // Track which filter sections are open to conditionally load APIs
  const [openSections, setOpenSections] = useState<string[]>([
    'statuses',
    'chargeSpeed',
  ]);

  // Track if we've already fetched operators on mount
  const hasAutoFetchedOperators = useRef(false);

  // Track if we've already applied URL operator selections
  const hasAppliedUrlOperators = useRef(false);

  // Fetch location info - enabled by default, follows same pattern as charge points
  const {
    data: locationInfo,
    isLoading: isLoadingOperators,
    refetch: refetchOperators,
  } = useLocationInformation({
    enabled: true, // Auto-fetch on map center changes (with built-in debounce)
  });

  // Auto-fetch operators if operatorIds in URL on mount (for deep linking)
  useEffect(() => {
    const urlOperatorIds = searchParams.get('operatorIds');

    if (urlOperatorIds && !hasAutoFetchedOperators.current) {
      hasAutoFetchedOperators.current = true;

      // Fetch operators (don't auto-open the section, that's handled by filter_ui param)
      refetchOperators();
    }
  }, [searchParams, refetchOperators]); // Re-run if searchParams change

  // After operators are loaded, apply URL operator selections (ONCE)
  useEffect(() => {
    const urlOperatorIds = searchParams.get('operatorIds');

    if (
      urlOperatorIds &&
      locationInfo?.operators &&
      locationInfo.operators.length > 0 &&
      !hasAppliedUrlOperators.current
    ) {
      hasAppliedUrlOperators.current = true;

      const requestedIds = urlOperatorIds
        .split(',')
        .map(Number)
        .filter((id) => !isNaN(id));

      // Find which operator suggestions match the requested IDs
      // IDs from URL could be either:
      // 1. Suggestion IDs (operator.id) - direct match
      // 2. Actual operator IDs (within operator.operatorIds array) - check if contained
      const matchingSuggestionIds: number[] = [];

      locationInfo.operators.forEach((operator) => {
        // Check if URL ID matches this suggestion ID directly
        const isSuggestionIdMatch = requestedIds.includes(operator.id);

        // Check if this operator suggestion contains any of the requested operator IDs
        const hasMatchingOperatorId = operator.operatorIds?.some((opId) =>
          requestedIds.includes(opId)
        );

        if (isSuggestionIdMatch || hasMatchingOperatorId) {
          matchingSuggestionIds.push(operator.id);
        }
      });

      // Apply the selections in a single batch update (avoid multiple URL updates)
      if (matchingSuggestionIds.length > 0) {
        const matchingOperators = matchingSuggestionIds
          .map((suggestionId) =>
            locationInfo.operators.find((op) => op.id === suggestionId)
          )
          .filter((op): op is NonNullable<typeof op> => op !== undefined);

        // Use batch set to enhance the filter with suggestion IDs and team IDs
        useFilterStore.getState().setOperatorSuggestions(matchingOperators);

        // On a restricted embed these ids are part of the host-pinned scope, so
        // fold them into the reset baseline too - resolving operatorIds against
        // the suggestions happens here, after useUrlParams has already run.
        if (searchParams.get('operator_scope') === 'restrict') {
          const state = useFilterStore.getState();
          state.setUrlScope({
            operatorSuggestionIds: [...state.selectedOperatorSuggestionIds],
            operatorIds: [...state.selectedOperatorIds],
            teamIds: [...state.selectedTeamIds],
          });
        }
      }
      // Note: Operator IDs are already set from URL in useUrlParams, so filtering will still work
      // even if we don't find matching suggestions (location-independent filtering)
    }
  }, [locationInfo, searchParams]); // Re-run when operators are loaded

  // Fetch operators when section is first opened
  const handleSectionChange = (sections: string[]) => {
    setOpenSections(sections);

    // If operators section is being opened for the first time, trigger fetch
    const wasOperatorsOpen = openSections.includes('operators');
    const isOperatorsNowOpen = sections.includes('operators');

    if (!wasOperatorsOpen && isOperatorsNowOpen) {
      refetchOperators();
    }

    // If price section is being opened for the first time, trigger fetch
    const wasPriceOpen = openSections.includes('price');
    const isPriceNowOpen = sections.includes('price');

    if (!wasPriceOpen && isPriceNowOpen) {
      refetchOperators();
    }
  };

  const toggleFilterPanel = useFilterStore((s) => s.toggleFilterPanel);

  // Monta always appears first in the operator filter; any Monta entry the API
  // returns is de-duped. See buildOperatorList.
  const allOperators = buildOperatorList(locationInfo?.operators);

  return (
    <div
      data-testid="filter-panel"
      className={inBottomSheet ? '' : 'flex min-h-0 flex-1 flex-col'}
    >
      {/* Header - hidden inside the bottom sheet (the sheet renders the title +
          close button); a compact Reset stays reachable */}
      {!inBottomSheet ? (
        <div className="shrink-0 px-4 py-3 border-b border-ink/10 flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg text-ink">
            {t('filters.title')}
          </h2>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="text-sm text-ink hover:text-ink/80 font-medium transition-colors"
              >
                {t('filters.reset')}
              </button>
            )}
            <button
              onClick={toggleFilterPanel}
              className="text-muted hover:text-ink transition-colors p-1 rounded hover:bg-ink/5"
              aria-label="Close filters"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        hasActiveFilters && (
          <div className="flex justify-end pt-2">
            <button
              onClick={resetFilters}
              className="text-sm text-ink hover:text-ink/80 font-medium transition-colors"
            >
              {t('filters.reset')}
            </button>
          </div>
        )
      )}
      {/* Location context (country, pin toggle, address/coords) - drives the
          operators list and price currency, so it's pinned near the top */}
      {showLocationDetails && (
        <div className={`shrink-0 pt-4 ${inBottomSheet ? '' : 'px-4'}`}>
          <LocationBlock locationInfo={locationInfo} />
        </div>
      )}

      {/* Filter sections - on desktop this flexes to fill the panel and owns
          the scroll; inside the bottom sheet the sheet body owns the scroll */}
      <div
        className={`pt-4 pb-4 divide-y divide-ink/10 ${
          inBottomSheet ? '' : 'px-4 flex-1 min-h-0 overflow-y-auto'
        }`}
      >
        <Accordion.Root
          type="multiple"
          value={openSections}
          onValueChange={handleSectionChange}
        >
          <FilterSection title={t('filters.statuses.title')} value="statuses">
            <AvailabilityFilter />
          </FilterSection>
          {showAutoChargeFilter && (
            <FilterSection
              title={t('filters.autoCharge.title')}
              value="autoCharge"
            >
              <div className="space-y-3">
                <Switch
                  checked={showAutoCharge}
                  onCheckedChange={setShowAutoCharge}
                  label={t('filters.autoCharge.enabled')}
                />
              </div>
            </FilterSection>
          )}
          <FilterSection
            title={t('filters.chargeSpeed.title')}
            value="chargeSpeed"
          >
            <ChargeSpeedFilter />
          </FilterSection>

          {showPriceFilter &&
            searchParams.get('operator_scope') !== 'restrict' && (
              <FilterSection title={t('filters.price.title')} value="price">
                <PriceFilter />
              </FilterSection>
            )}
          {showOperatorFilter &&
            searchParams.get('operator_scope') !== 'restrict' && (
              <FilterSection
                title={t('filters.operators.title')}
                value="operators"
              >
                <OperatorFilter
                  operators={allOperators}
                  isLoading={isLoadingOperators}
                />
              </FilterSection>
            )}
        </Accordion.Root>
      </div>
    </div>
  );
}
